// src/context/FinancialContext.js
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { collection, doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { useUserData } from './UserContext';
import { FinancialService } from '../services/financialService';
import { getPaths } from '../firebase.paths'; 
import { globalAppId } from '../utils/constants';
import { db } from '../firebase'; 
import toast from 'react-hot-toast'; 

const FinancialContext = createContext();
export const useFinancialData = () => useContext(FinancialContext);

export const FinancialProvider = ({ children }) => {
    const { userProfile, auth } = useAuth();
    const { refreshLeaderboard } = useUserData();
    const [transactions, setTransactions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const paths = useMemo(() => getPaths(globalAppId), []);

    // Refs para controlar paginação sem causar re-renders no useCallback
    const lastDocRef = useRef(null);
    const isLoadingRef = useRef(false);

    // --- CARREGAMENTO ---
    // fetchTransactions tem deps vazias pois usa refs internamente,
    // evitando o loop: lastDoc muda → nova ref → useEffect dispara novamente
    const fetchTransactions = useCallback(async (isRefresh = false) => {
        if (!db || isLoadingRef.current) return;
        isLoadingRef.current = true;
        setIsLoading(true);
        try {
            const { data, lastDoc: newLast } = await FinancialService.getTransactions(
                isRefresh ? null : lastDocRef.current
            );
            setTransactions(prev => isRefresh ? data : [...prev, ...data]);
            lastDocRef.current = newLast;
            setHasMore(data.length === 50);
        } catch (error) {
            console.error("Erro ao buscar transações:", error);
        } finally {
            isLoadingRef.current = false;
            setIsLoading(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- fetchTransactions usa refs internamente, sem deps reativas

    useEffect(() => {
        if (auth.currentUser) fetchTransactions(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchTransactions é estável (deps vazias), incluí-la causaria loop
    }, [auth.currentUser]);

    // --- ENVIO DE KUDOS ---
    const handleSendKudos = useCallback(async (targetUserId, message) => {
        if (!targetUserId) throw new Error("Selecione um colega.");
        if (!message) throw new Error("Escreva um motivo.");
        if (!auth.currentUser) throw new Error("Usuário não autenticado.");

        const senderId = auth.currentUser.uid;
        const senderName = userProfile?.name || 'Colega';

        try {
            await FinancialService.sendKudos(senderId, senderName, targetUserId, message);

            // Optimistic update: o ID do kudos é previsível — injeta direto no estado
            // sem re-buscar os 50 documentos do servidor.
            const now = new Date();
            const lockId = `kudos_${senderId}_${now.getFullYear()}_${now.getMonth() + 1}`;
            const optimisticTx = {
                id:            lockId,
                targetUserId,
                targetUserName: 'Colega',
                adminId:        senderId,
                adminName:      senderName,
                pointsChange:   50,
                creditsChange:  50,
                reason:         `Solicitação de Valeu!: ${message}`,
                type:           'kudos_request',
                status:         'pending',
                createdAt:      now,
            };
            setTransactions(prev => [optimisticTx, ...prev]);

            toast.success("Valeu enviado com sucesso!");
        } catch (error) {
            console.error("Erro no handleSendKudos:", error);
            throw error;
        }
    }, [auth.currentUser, userProfile]);

    // --- OUTRAS FUNÇÕES ---

    // Versão "silenciosa": sem refresh automático de leaderboard/transações.
    // Usada internamente pelo batch para evitar N refreshes num loop —
    // o chamador decide quando atualizar (uma vez, após o loop completo).
    const _updatePointsSilent = useCallback(async (targetId, amount, reason) => {
        await FinancialService.updatePoints(targetId, amount, reason, userProfile?.name);
    }, [userProfile]);

    const handleUpdatePointsAndCredits = useCallback(async (targetId, amount, reason) => {
        await _updatePointsSilent(targetId, amount, reason);
        await refreshLeaderboard();
        fetchTransactions(true);
    }, [_updatePointsSilent, fetchTransactions, refreshLeaderboard]);

    const handlePenalizePoints = useCallback(async (targetId, amount, reason) => {
        await FinancialService.updatePoints(targetId, -Math.abs(amount), `Penalidade: ${reason}`, userProfile?.name);
        await refreshLeaderboard();
        fetchTransactions(true);
    }, [userProfile, fetchTransactions, refreshLeaderboard]);

    const handleResetUserPoints = useCallback(async (targetId, reason) => {
        await FinancialService.resetPoints(targetId, reason, userProfile?.name);
        await refreshLeaderboard();
        fetchTransactions(true);
    }, [userProfile, fetchTransactions, refreshLeaderboard]);

    const handleApproveKudos = useCallback(async (id) => {
        await FinancialService.approveKudos(id, userProfile?.name);
        setTransactions(prev => prev.map(t => t.id === id ? { ...t, status: 'approved' } : t));
        await refreshLeaderboard(); // usuário recebeu pontos
    }, [userProfile, refreshLeaderboard]);

    const handleDeleteTransactionWithReversal = useCallback(async (id) => {
        await FinancialService.deleteTransactionWithReversal(id);
        setTransactions(prev => prev.filter(t => t.id !== id));
        await refreshLeaderboard(); // pontos podem ter sido revertidos
    }, [refreshLeaderboard]);

    const handleDeleteTransaction = useCallback(async (id) => {
        await FinancialService.deleteTransaction(id);
        setTransactions(prev => prev.filter(t => t.id !== id));
    }, []);

    const handleEditTransaction = useCallback(async (id, data) => {
        await FinancialService.updateTransaction(id, data);
        // Optimistic update — evita re-fetch de 50 docs para uma edição local
        setTransactions(prev => prev.map(t => t.id === id ? { ...t, ...data } : t));
    }, []);

    const handleProcessInvoice = useCallback(async (invoiceData, items) => {
        // Lógica simplificada de invoice para focar no erro principal
        const { vendor, description, amount, date } = invoiceData;
        const totalAmount = -Math.abs(Number(amount)); 

        await runTransaction(db, async (transaction) => {
            const configRef = doc(db, paths.config, 'shopSettings');
            const configSnap = await transaction.get(configRef);
            
            items.forEach(item => {
                const currentData = configSnap.exists() ? configSnap.data()[item.name] : {};
                const currentStock = currentData?.stock || 0;
                
                transaction.set(configRef, { 
                    [item.name]: { 
                        stock: currentStock + Number(item.qty), 
                        price: Number(item.price), 
                        points: currentData?.points || 0 
                    } 
                }, { merge: true });

                const stockLogRef = doc(collection(db, paths.transactions));
                transaction.set(stockLogRef, {
                    type: 'stock_entry', itemName: item.name, qty: Number(item.qty), cost: Number(item.price * item.qty),
                    vendor: vendor, adminName: userProfile?.name || 'Admin', createdAt: date ? new Date(date) : serverTimestamp(), status: 'approved'
                });
            });

            const invoiceRef = doc(collection(db, paths.transactions));
            transaction.set(invoiceRef, {
                type: 'invoice_expense', description: description, adminName: userProfile?.name || 'Admin',
                valueBRL: totalAmount, vendor: vendor, itemsDetail: items, createdAt: date ? new Date(date) : serverTimestamp(), status: 'approved'
            });
        });
        fetchTransactions(true);
    }, [paths, userProfile, fetchTransactions]); // eslint-disable-line react-hooks/exhaustive-deps -- db é valor de escopo externo, não reativo

    const handleDeliverItem = useCallback(async (id) => {
        await FinancialService.deliverItem(id);
        setTransactions(prev => prev.map(t => t.id === id ? { ...t, deliveryStatus: 'delivered' } : t));
    }, []);

    const handleToggleDelivery = useCallback(async (id, currentStatus) => {
        const newStatus = await FinancialService.toggleDelivery(id, currentStatus);
        setTransactions(prev => prev.map(t => t.id === id ? { ...t, deliveryStatus: newStatus } : t));
    }, []);

    const handleProcessReturn = useCallback(async (id, data) => {
        await FinancialService.processReturn(id, data, userProfile?.name);
        fetchTransactions(true);
    }, [userProfile, fetchTransactions]);

    // Lança NF com rateio de frete/extras proporcional aos itens
    const handleSubmitInvoice = useCallback(async ({ supplier, invoiceNumber, items, freight, otherExpenses }) => {
        const totalItemsValue = items.reduce((acc, i) => acc + (i.qty * i.unitCost), 0);
        const extra  = (freight || 0) + (otherExpenses || 0);
        const rateio = totalItemsValue > 0 ? extra / totalItemsValue : 0;

        await runTransaction(db, async (t) => {
            const configRef  = doc(db, paths.config, 'shopSettings');
            const configSnap = await t.get(configRef);
            const currentData = configSnap.exists() ? configSnap.data() : {};

            for (const item of items) {
                const iConf    = currentData[item.name] || {};
                const finalCost = item.unitCost * (1 + rateio);
                t.set(configRef, {
                    [item.name]: { ...iConf, stock: Number(iConf.stock || 0) + item.qty, price: finalCost }
                }, { merge: true });
                t.set(doc(collection(db, paths.transactions)), {
                    type: 'stock_entry', itemName: item.name, qty: item.qty,
                    cost: finalCost * item.qty, invoiceNumber, vendor: supplier,
                    adminName: userProfile?.name || 'Admin',
                    createdAt: serverTimestamp(), status: 'approved',
                });
            }
        });
        fetchTransactions(true);
    }, [paths, userProfile, fetchTransactions]); // eslint-disable-line react-hooks/exhaustive-deps

    // Processa devolução de item resgatado na loja (troca, reembolso ou avaria).
    // Centraliza a lógica que estava diretamente no StockTab com imports do Firebase.
    const handleStockReturn = useCallback(async (returnModal, returnType) => {
        const { id, targetUserId, creditsChange, itemRefunded } = returnModal;
        const cost = Math.abs(creditsChange);
        await runTransaction(db, async (transaction) => {
            const transRef  = doc(db, paths.transactions, id);
            const userRef   = doc(db, paths.users, targetUserId);
            const configRef = doc(db, paths.config, 'shopSettings');
            const logRef    = doc(collection(db, paths.transactions));

            if (returnType === 'exchange') {
                transaction.update(transRef, { deliveryStatus: 'exchanged', updatedAt: serverTimestamp() });
                transaction.set(logRef, { type: 'return_exchange', reason: `Troca: ${itemRefunded}`, targetUserId, adminName: userProfile?.name, createdAt: serverTimestamp() });
            } else if (returnType === 'refund') {
                const userSnap   = await transaction.get(userRef);
                const configSnap = await transaction.get(configRef);
                if (userSnap.exists())
                    transaction.update(userRef, { credits: (userSnap.data().credits || 0) + cost });
                if (configSnap.exists()) {
                    const curr = configSnap.data()[itemRefunded]?.stock || 0;
                    transaction.set(configRef, { [itemRefunded]: { ...configSnap.data()[itemRefunded], stock: curr + 1 } }, { merge: true });
                }
                transaction.update(transRef, { deliveryStatus: 'returned_refunded' });
                transaction.set(logRef, { type: 'return_refund', reason: `Reembolso: ${itemRefunded}`, creditsChange: cost, targetUserId, adminName: userProfile?.name, createdAt: serverTimestamp() });
            } else if (returnType === 'defect') {
                const userSnap = await transaction.get(userRef);
                if (userSnap.exists())
                    transaction.update(userRef, { credits: (userSnap.data().credits || 0) + cost });
                transaction.update(transRef, { deliveryStatus: 'returned_defect' });
                transaction.set(logRef, { type: 'return_defect', reason: `Avaria: ${itemRefunded}`, creditsChange: cost, targetUserId, adminName: userProfile?.name, createdAt: serverTimestamp() });
            }
        });
        if (returnType !== 'exchange') await refreshLeaderboard();
        fetchTransactions(true);
    }, [paths, userProfile, refreshLeaderboard, fetchTransactions]); // eslint-disable-line react-hooks/exhaustive-deps

    // Exclui entrada de estoque e reverte a quantidade no shopSettings
    const handleDeleteStockEntry = useCallback(async (t) => {
        await runTransaction(db, async (tr) => {
            const cRef  = doc(db, paths.config, 'shopSettings');
            const cSnap = await tr.get(cRef);
            if (cSnap.exists()) {
                const d     = cSnap.data();
                const iConf = d[t.itemName] || {};
                tr.set(cRef, {
                    [t.itemName]: { ...iConf, stock: Math.max(0, Number(iConf.stock || 0) - Number(t.qty)) }
                }, { merge: true });
            }
            tr.delete(doc(db, paths.transactions, t.id));
        });
        setTransactions(prev => prev.filter(tx => tx.id !== t.id));
    }, [paths]); // eslint-disable-line react-hooks/exhaustive-deps

    const value = useMemo(() => ({
        transactions,
        isLoading,
        hasMore,
        fetchMore: () => fetchTransactions(false),
        refresh: () => fetchTransactions(true),

        handleSendKudos,

        // Versão silenciosa para uso em loops (batch) — sem refresh por iteração
        _updatePointsSilent,
        refreshLeaderboard,

        handleUpdatePointsAndCredits,
        handlePenalizePoints,
        handleResetUserPoints,
        handleApproveKudos,
        handleDeleteTransactionWithReversal,
        handleDeleteTransaction,
        handleEditTransaction,
        handleProcessInvoice,
        handleDeliverItem,
        handleToggleDelivery,
        handleProcessReturn,
        handleStockReturn,
        handleSubmitInvoice,
        handleDeleteStockEntry,
    }), [
        transactions, isLoading, hasMore, fetchTransactions,
        handleSendKudos,
        _updatePointsSilent, refreshLeaderboard,
        handleUpdatePointsAndCredits, handlePenalizePoints, handleResetUserPoints,
        handleApproveKudos, handleDeleteTransactionWithReversal,
        handleDeleteTransaction, handleEditTransaction, handleProcessInvoice,
        handleDeliverItem, handleToggleDelivery, handleProcessReturn,
        handleStockReturn, handleSubmitInvoice, handleDeleteStockEntry,
    ]);

    return <FinancialContext.Provider value={value}>{children}</FinancialContext.Provider>;
};
